import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicApi } from '../services/api';
import useLiveKit from '../hooks/useLiveKit';
import VideoPlayer from '../components/VideoPlayer';
import Controls from '../components/Controls';
import Chat from '../components/Chat';
import { VirtualBackground } from '@livekit/track-processors';
import { Mic, MicOff, Camera, CameraOff } from 'lucide-react';
import { logDiagnostic, setDiagnosticsContext, attachGlobalErrorHandlers } from '../services/diagnostics';
import { flipImageHorizontally } from '../services/flipImage';

/**
 * Posts a Whereby-compatible event to the parent window so the HelpGlobal
 * host page can react (redirect the patient, unmount the iframe, etc).
 * No-op when the embed is not framed.
 */
function postEmbedEvent(type, payload = {}) {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type, ...payload }, '*');
    }
  } catch { /* cross-origin issues - ignore */ }
}

/**
 * Checks if the browser already has persistent permission for camera AND
 * microphone on this origin (the user authorized in a prior session on this
 * machine). Returns true only when both report `granted`. Safely returns false
 * on Safari <16 or any platform where the Permissions API is unavailable for
 * media devices, so the usual device-check screen still shows as fallback.
 */
async function checkCachedPermissions() {
  try {
    if (!navigator.permissions?.query) return false;
    const [mic, cam] = await Promise.all([
      navigator.permissions.query({ name: 'microphone' }),
      navigator.permissions.query({ name: 'camera' }),
    ]);
    return mic.state === 'granted' && cam.state === 'granted';
  } catch { return false; }
}

/**
 * Detecta browsers in-app de redes sociais (Instagram, Facebook, TikTok).
 * Esses webviews tem WebRTC reduzido e bloqueiam autoplay de audio - paciente
 * conecta, publica camera/mic, mas nao escuta o medico (audio.play() do
 * <audio> remoto rejeita silencioso com NotAllowedError porque o gesto do
 * usuario expira antes do track remoto chegar).
 *
 * Retorna o nome do app detectado, ou null se for browser normal.
 *
 * Conservador de proposito: so bloqueia os 3 confirmados como problematicos
 * em producao. NAO bloqueia 'wv' generico porque Chrome Custom Tabs e Safari
 * View Controller tambem tem 'wv'/'AppleWebKit' e funcionam bem.
 */
function detectInAppWebview() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/Instagram/i.test(ua)) return 'Instagram';
  if (/FBAN|FBAV/.test(ua)) return 'Facebook';
  if (/musical_ly|TikTok|BytedanceWebview/i.test(ua)) return 'TikTok';
  return null;
}

// Formata duracao em ms pra "MM:SS" sempre (ex: 00:30, 08:30, 75:04 se
// passar de 1h - minutos seguem acumulando). Usado na tela de atendimento
// encerrado pra mostrar quanto durou a consulta.
function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(m)}:${pad(s)}`;
}

function formatClock(ts) {
  if (!ts) return '--:--';
  const d = new Date(ts);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// Mostra na tela de pre-entrada do medico se o paciente ja entrou na sala.
// Atualizado por polling em EmbedPage (3s). Design deliberado: verde pulsante
// quando paciente chega (facil de bater o olho); laranja quando aguardando
// (mais visivel que cinza sobre fundo escuro).
function PatientPresenceBadge({ present }) {
  if (present) {
    return (
      <div className="flex items-center justify-center gap-2 bg-emerald-600/25 border border-emerald-500/60 text-emerald-100 rounded-xl px-3 py-2.5 text-sm font-medium">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
        </span>
        <span>Paciente ja esta na sala</span>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center gap-2 bg-amber-500/15 border border-amber-500/50 text-amber-200 rounded-xl px-3 py-2.5 text-sm font-medium">
      <span className="inline-flex rounded-full h-2.5 w-2.5 bg-amber-400 animate-pulse"></span>
      <span>Aguardando paciente entrar</span>
    </div>
  );
}

function DeviceStatusRow({ label, state }) {
  // state: null = pendente, true = ok, false = bloqueado
  let color = 'bg-slate-700 text-slate-300';
  let icon = '•';
  let text = 'Aguardando teste';
  if (state === true) { color = 'bg-emerald-600/20 text-emerald-300'; icon = '✓'; text = 'Permitido'; }
  if (state === false) { color = 'bg-red-600/20 text-red-300'; icon = '✕'; text = 'Bloqueado'; }
  const isMic = label.toLowerCase().includes('microfone') || label.toLowerCase().includes('mic');
  const Icon = state === false
    ? (isMic ? MicOff : CameraOff)
    : (isMic ? Mic : Camera);
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl ${color}`}>
      <Icon size={20} />
      <span className="flex-1 font-medium">{label}</span>
      <span className="text-xs font-bold">{icon} {text}</span>
    </div>
  );
}

/**
 * Whereby-compatible embed page.
 *
 * URL params supported (all optional):
 *   displayName   - nome do participante (pula tela "digite nome")
 *   avatarUrl     - foto do participante
 *   role          - "host" ou "participant"
 *   roomKey       - secret que identifica host (vem de POST /api/rooms hostRoomUrl)
 *   chat          - "on" | "off"      (default on)
 *   people        - "on" | "off"      (default on)
 *   leaveButton   - "on" | "off"      (default on)
 *   minimal       - "on"              (interface mínima)
 *   audio         - "off"             (entra com mic mudo)
 *   video         - "off"             (entra com camera desligada)
 *   background    - "off"             (fundo transparente)
 *   screenshare   - "on" | "off"
 */
export default function EmbedPage() {
  const { roomId } = useParams();
  const [params] = useSearchParams();

  const config = useMemo(() => ({
    displayName: params.get('displayName') || '',
    avatarUrl: params.get('avatarUrl') || '',
    role: params.get('role') || 'participant',
    roomKey: params.get('roomKey') || '',
    externalId:  params.get('externalId')  || '',
    crmConselho: params.get('crmConselho') || '',
    crmNumero:   params.get('crmNumero')   || '',
    crmEstado:   params.get('crmEstado')   || '',
    chatOn: params.get('chat') !== 'off',
    peopleOn: params.get('people') !== 'off',
    leaveButtonOn: params.get('leaveButton') !== 'off',
    minimal: params.get('minimal') === 'on',
    startAudioOff: params.get('audio') === 'off',
    startVideoOff: params.get('video') === 'off',
    transparentBg: params.get('background') === 'off',
    screenshareOn: params.get('screenshare') !== 'off',
    // testIce: filtro de iceServers pra debug/sandbox. Default null = sem filtro.
    // Valores aceitos: cloudflare | coturn | no-turn (qualquer outro = ignorado)
    testIce: params.get('testIce') || null,
  }), [params]);

  // Hooks globais de erro + contexto inicial pra diagnostico (roomId e role).
  // Identity chega depois do /join; atualiza o contexto la.
  useEffect(() => {
    attachGlobalErrorHandlers();
    setDiagnosticsContext({
      roomId,
      role: config.role,
      displayName: config.displayName || null,
      externalId: config.externalId || null,
    });
    logDiagnostic({
      severity: 'info',
      type: 'page_view',
      message: `EmbedPage mounted role=${config.role}`,
      metadata: {
        url: window.location.href,
        screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        online: navigator.onLine,
      },
    });
  }, [roomId, config.role, config.displayName, config.externalId]);

  const [roomInfo, setRoomInfo] = useState(null);
  const [userName, setUserName] = useState(config.displayName);
  const [joined, setJoined] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  // Tempo decorrido em ms desde que o loading comecou. Usado pra trocar a
  // mensagem do spinner (Conectando -> Verificando -> Quase la), dando
  // feedback visual de progresso pro paciente nao achar que travou e
  // ficar dando F5 - feedback fixo num spinner so amplifica essa sensacao
  // quando a 1a tentativa cai no auto-retry silencioso e o tempo total
  // chega a ~9-11s.
  const [loadingElapsedMs, setLoadingElapsedMs] = useState(0);
  const [chatToken, setChatToken] = useState('');
  const [myIdentity, setMyIdentity] = useState('');
  const [meetingEnded, setMeetingEnded] = useState(false);
  // Inicio/fim da consulta gravados em timestamp (ms). Usado pra mostrar
  // duracao pro medico na tela de "Atendimento encerrado" - paciente nao
  // precisa ver. Guardado em ref tambem pra emitEndOnce poder ler o valor
  // sem depender de re-render.
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [sessionEndedAt, setSessionEndedAt] = useState(null);
  const sessionStartedAtRef = useRef(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [templateUrl, setTemplateUrl] = useState('');    // fundo personalizado do medico
  const [templateActive, setTemplateActive] = useState(false);
  // Auto-Picture-in-Picture: quando ligado, o remote <video> recebe o atributo
  // HTML autoPictureInPicture e o browser abre PiP automaticamente na hora
  // que a aba perde visibilidade.
  //
  // Feature e kill-switch do admin: persistido em DoctorProfile.PipAutoEnabled
  // e carregado no /join. Admin pode desligar pra esconder a funcionalidade
  // por medico (ex: medico reclama que PiP abre sem querer). Medico nao tem
  // toggle proprio - quem decide e o painel administrativo.
  const [pipAutoEnabled, setPipAutoEnabled] = useState(false);
  // Role efetivo = o que o backend retornou no /join. Pode ser diferente do
  // config.role (que veio da URL) quando o sandbox do integrador nao anexa
  // role=host no iframe mas o backend promove via roomKey. Usar este pra
  // decidir visibilidade dos botoes especificos de host/paciente.
  const [effectiveRole, setEffectiveRole] = useState('participant');

  // Chat is only available when BOTH the client has it enabled in admin AND the
  // caller passed chat=on in the URL. Either side can turn it off. Default to
  // the URL param until roomInfo loads to avoid a flash of hidden button.
  const chatAvailable = config.chatOn && (roomInfo?.chat?.enabled !== false);

  // Ref pro <VideoPlayer> remoto — usado pra acionar Picture-in-Picture do
  // paciente (pra o medico manter o video visivel enquanto abre MEVO/outra aba).
  const remoteVideoRef = useRef(null);
  const [pipActive, setPipActive] = useState(false);

  useEffect(() => {
    const handlePipChange = () => {
      setPipActive(remoteVideoRef.current?.isPipActive() ?? false);
    };
    document.addEventListener('enterpictureinpicture', handlePipChange);
    document.addEventListener('leavepictureinpicture', handlePipChange);
    return () => {
      document.removeEventListener('enterpictureinpicture', handlePipChange);
      document.removeEventListener('leavepictureinpicture', handlePipChange);
    };
  }, []);

  const endEmittedRef = useRef(false);
  const emitEndOnce = useCallback((reason) => {
    if (endEmittedRef.current) return;
    endEmittedRef.current = true;
    // Congela o instante de termino AGORA pra duracao renderizar estavel
    // mesmo que o componente re-monte. Prioriza ref porque state ainda pode
    // nao ter propagado se o room_closed chegou no mesmo tick do join.
    const endedAt = Date.now();
    setSessionEndedAt(endedAt);
    postEmbedEvent('end_meeting', {
      roomId,
      reason,
      // Expoe duracao pra HelpGlobal logar / salvar no prontuario dela se quiser.
      durationMs: sessionStartedAtRef.current ? endedAt - sessionStartedAtRef.current : null,
    });
    // Show an explicit "ended" screen so a standalone tab (no parent listener
    // to redirect the patient) does not get stuck spinning on "Conectando...".
    // When embedded, the parent should unmount the iframe on end_meeting before
    // this screen is visible for more than a moment.
    setMeetingEnded(true);
  }, [roomId]);

  const {
    localVideoTrack, remoteVideoTrack, remoteAudioTrack,
    isRemoteConnected, connectionStatus, remoteName,
    localMic, localCam, remoteMic, remoteCam, cameraFacing,
    connectionEpoch,
    connect, disconnect, toggleCamera, toggleMic, setProcessor, switchCamera,
  } = useLiveKit({
    onEvent: (type, data) => {
      if (type === 'room_closed') {
        // serverClosed = encerramento intencional do outro lado (medico saiu,
        // sala deletada, sala fechou). E o sinal pra HelpGlobal marcar a
        // consulta como realizada e redirecionar o paciente.
        if (data.serverClosed) {
          emitEndOnce(`server:${data.reason}`);
          return;
        }
        // isFailure = drop tecnico (ICE timeout, signal close, join failure).
        // NAO postar end_meeting: o livekit-client tem reconnectPolicy que
        // vai tentar de volta sozinho. Postar end_meeting aqui fazia o iframe
        // pai (HelpGlobal) fechar o embed e jogar o paciente pra tela "Entrar"
        // depois de qualquer drop momentaneo - paciente tinha que clicar
        // entrar 2-3x ate uma tentativa nao falhar nos primeiros segundos.
        if (data.isFailure) {
          postEmbedEvent('connection_failure', { roomId, reason: data.reason });
          return;
        }
        // reason 1 (CLIENT_INITIATED): o proprio paciente fechou. Sem efeito
        // colateral aqui - o usuario ja sabe que saiu.
        return;
      }
      // Forward everything else (participant_join, screenshare_*, mic/camera_toggle) as-is.
      postEmbedEvent(type, { roomId, ...data });
    },
  });

  // Cronometro ao vivo da consulta (so pro medico). Tick de 1s via setInterval.
  // Para de rodar quando meetingEnded pra segurar o valor final no lugar.
  // Usa sessionStartedAtRef (nao state) pra evitar stale closure dentro do tick.
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => {
    if (!sessionStartedAt || meetingEnded) return;
    const tick = () => {
      const start = sessionStartedAtRef.current;
      if (start) setElapsedMs(Date.now() - start);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sessionStartedAt, meetingEnded]);

  // Heuristica "tela preta com Conectado": se depois de conectado passou >15s
  // e ninguem publicou track remoto, provavelmente o outro lado nao chegou ou
  // tem problema de codec/subscribe. Registra 1x por sessao pra nao inundar.
  const noRemoteWarnedRef = useRef(false);
  useEffect(() => {
    if (connectionStatus !== 'connected') {
      noRemoteWarnedRef.current = false;
      return;
    }
    if (isRemoteConnected) {
      noRemoteWarnedRef.current = false;
      return;
    }
    const t = setTimeout(() => {
      if (!noRemoteWarnedRef.current && !isRemoteConnected) {
        noRemoteWarnedRef.current = true;
        logDiagnostic({
          severity: 'warn',
          type: 'no_remote_track',
          message: 'Conectado ha 15s+ mas sem track remoto - possivel sala vazia ou falha de subscribe/codec',
        });
      }
    }, 15000);
    return () => clearTimeout(t);
  }, [connectionStatus, isRemoteConnected]);

  useEffect(() => {
    let cancelled = false;
    // Detecta in-app browser pra log/metricas. Antes bloqueavamos a entrada
    // - mas isso assumia que era impossivel funcionar. Em vez disso, deixamos
    // entrar e usamos unlock agressivo de audio em qualquer toque do usuario
    // (useEffect mais abaixo) + botao manual "Ativar som". Se ainda assim
    // falhar pra alguns webviews especificos, o paciente pode tocar o botao
    // amarelo no topo pra forcar play() em todos os <audio>/<video>.
    const inApp = detectInAppWebview();
    if (inApp) {
      logDiagnostic({
        severity: 'warn',
        type: 'in_app_browser_detected',
        message: `In-app browser detectado: ${inApp} - tentando rodar com unlock agressivo`,
        metadata: { app: inApp, userAgent: navigator.userAgent },
      });
    }
    (async () => {
      try {
        const info = await publicApi.getRoomInfo(roomId);
        if (cancelled) return;
        if (info.error) { setError(info.error); return; }
        setRoomInfo(info);
      } catch { if (!cancelled) setError('Sala nao encontrada.'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // "E medico nessa sala?" - role=host na URL OU roomKey presente. Backend
  // promove via roomKey, entao aceitar ambos. Usado pra decidir se polla
  // presenca do paciente e se mostra badge/bipe.
  const isHost = config.role === 'host' || !!config.roomKey;

  // Polling de presenca do paciente enquanto o medico esta na tela de pre-
  // entrada. Bate no GET /room/{id} a cada 3s. O endpoint e leve (so count
  // agrupado por role). Para assim que o medico clicar Entrar (joined=true)
  // ou a sala der erro.
  const [patientPresent, setPatientPresent] = useState(false);
  const prevPatientPresentRef = useRef(false);
  // AudioContext criado sob demanda - se o medico ainda nao interagiu com a
  // pagina, browser recusa play() por policy de autoplay. Um listener global
  // de click/keydown destrava (resume) no primeiro gesto.
  const audioCtxRef = useRef(null);
  const ensureAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      try {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return null;
        audioCtxRef.current = new Ctor();
      } catch { return null; }
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => { /* ignore */ });
    }
    return audioCtxRef.current;
  }, []);
  useEffect(() => {
    // Unlock agressivo de audio em qualquer gesto do usuario. Cobre o caso
    // do paciente em webview restritivo (Instagram, FB, TikTok, Safari iOS
    // sem gesto recente) onde <video>/<audio>.play() rejeita por autoplay
    // policy mesmo com o track ja attachado pelo VideoPlayer.
    //
    // O que fazemos a cada toque/click:
    //   1. ensureAudioCtx() - resume o AudioContext da app (beep de
    //      patient_arrived e qualquer Web Audio futuro)
    //   2. .play() em todos os <video> e <audio> do DOM - se algum estava
    //      rejeitado por NotAllowedError, agora destrava. Idempotente: se
    //      ja esta tocando, e no-op.
    //
    // touchstart cobre mobile (iOS/Android), click cobre desktop e fallback
    // mobile. keydown e raro mas mantido pro caso de teclado/acessibilidade.
    // Capture: true pra pegar o evento ANTES dele chegar nos handlers de
    // botoes - garante que mesmo se um botao com stopPropagation receber
    // primeiro, o unlock global ainda roda.
    const unlock = () => {
      ensureAudioCtx();
      try {
        document.querySelectorAll('audio, video').forEach((el) => {
          if (!el.paused) return;
          const p = el.play?.();
          if (p && typeof p.catch === 'function') p.catch(() => { /* ainda bloqueado - proximo gesto tenta */ });
        });
      } catch { /* defensivo */ }
    };
    window.addEventListener('touchstart', unlock, { capture: true, passive: true });
    window.addEventListener('click', unlock, { capture: true });
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('touchstart', unlock, { capture: true });
      window.removeEventListener('click', unlock, { capture: true });
      window.removeEventListener('keydown', unlock);
    };
  }, [ensureAudioCtx]);
  const playPatientArrivedBeep = useCallback(() => {
    const ctx = ensureAudioCtx();
    if (!ctx) return;
    try {
      // 2 beeps curtos ascendentes (880 -> 1175 Hz) pra ser perceptivel sem
      // assustar. Decai em ~0.4s cada, gap de 0.15s.
      const now = ctx.currentTime;
      const play = (startAt, freq) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, startAt);
        gain.gain.setValueAtTime(0, startAt);
        gain.gain.linearRampToValueAtTime(0.25, startAt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.35);
        osc.connect(gain).connect(ctx.destination);
        osc.start(startAt);
        osc.stop(startAt + 0.4);
      };
      play(now, 880);
      play(now + 0.18, 1175);
    } catch { /* ignora - nao vale quebrar a UI por beep */ }
  }, [ensureAudioCtx]);

  useEffect(() => {
    // So pro host. Intervalo fixo de 3s.
    // Continua rodando depois do joined porque participantConnected do tile
    // remoto (in-call) tambem e gated pelo webhook (patientPresent). Sem
    // manter o poll ativo, isRemoteConnected do SDK do LiveKit fica como
    // unica fonte e marca o paciente como presente cedo demais (durante o
    // handshake), antes do webhook participant_joined chegar no backend.
    if (!isHost || error) return undefined;
    let cancelled = false;
    // Se paciente ja estiver na sala na primeira leitura, NAO toca o bipe -
    // o bipe e so pro caso do medico estar esperando e o paciente chegar.
    // Sem esse guard, abrir a tela com paciente ja dentro dispararia bipe.
    let firstTick = true;
    const tick = async () => {
      try {
        const info = await publicApi.getRoomInfo(roomId);
        if (cancelled) return;
        if (info?.error) return;
        const present = !!info.patientPresent;
        // Bipe so faz sentido enquanto medico ainda nao entrou (alerta que o
        // paciente chegou na sala de espera). Apos joined, o medico ja esta
        // na chamada e veria o tile aparecer - bipe ai seria ruido.
        if (!joined && !firstTick && present && !prevPatientPresentRef.current) {
          playPatientArrivedBeep();
        }
        firstTick = false;
        prevPatientPresentRef.current = present;
        setPatientPresent(present);
        // Atualiza tambem o roomInfo - mantem branding/flags alinhados se
        // algo mudar no admin durante a espera (pouco comum mas barato).
        setRoomInfo(info);
      } catch { /* rede vacilou - proximo tick tenta de novo */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isHost, joined, error, roomId, playPatientArrivedBeep]);

  // Checagem de permissoes de camera/mic antes de entrar
  const [deviceCheck, setDeviceCheck] = useState({
    micGranted: null,  // null = nao testado, true/false = resultado
    camGranted: null,
    checking: false,
    errorMessage: '',
  });
  const [awaitingDeviceCheck, setAwaitingDeviceCheck] = useState(false);
  // Stream do deviceCheck mantido vivo ate o LiveKit assumir. Releasing o
  // stream com getTracks().stop() antes do LiveKit chamar getUserMedia()
  // de novo cria uma janela de tempo onde o browser libera o hardware do
  // mic e a re-aquisicao acontece em outro contexto - resultado e que o
  // primeiro pacote de audio publicado pro SFU sai mudo (issue #4379).
  // Telemedicinanew (referencia) mantem o stream ativo durante todo o
  // fluxo - replicamos esse padrao. O cleanup useEffect no fim libera
  // o stream quando o componente desmonta (sala fechada).
  const deviceStreamRef = useRef(null);
  // Confirmacao "Entrar" antes de cair na sala. Ativada por cliente via
  // admin (ApiClient.HostJoinConfirmEnabled) e so aplica pro role=host.
  // Paciente sempre entra direto se tem permissao. Se permissao nao existe,
  // o device check ja serve de confirmacao - esse flag nao se aplica la.
  const [awaitingHostConfirm, setAwaitingHostConfirm] = useState(false);

  // Quando veio displayName na URL, em vez de entrar direto, mostra tela de check
  // de dispositivos (somente se o cliente tiver habilitado essa opcao no admin).
  // Otimizacao: se o navegador ja tem permissao concedida pra camera+mic nessa
  // origem (= o medico ja autorizou em consulta anterior nesse PC), pula a tela
  // e entra direto. Mudar de PC ou navegador anonimo = tela aparece normalmente,
  // porque a consulta nativa da Permissions API reporta 'prompt' em vez de 'granted'.
  useEffect(() => {
    if (joined || loading || error) return;
    if (!config.displayName) return;
    if (!roomInfo) return;

    const deviceCheckEnabled = roomInfo?.deviceCheck?.enabled !== false;
    // Config do cliente: exige um botao "Entrar" antes de cair na sala quando
    // o medico ja tem permissoes cacheadas. Nao aplica pro paciente nem pro
    // fluxo de primeiro acesso (quando a tela de ativar camera ja faz as vezes
    // de confirmacao).
    //
    // Deteccao de "e medico": aceita role=host na URL OU roomKey presente.
    // HelpGlobal manda o iframe com roomKey no link do medico sem
    // necessariamente passar role=host - o backend promove via roomKey. Sem
    // aceitar roomKey aqui, a tela de confirmacao nunca aparecia.
    const isHost = config.role === 'host' || !!config.roomKey;
    const hostConfirmEnabled = isHost && roomInfo?.hostJoinConfirm?.enabled === true;

    if (!deviceCheckEnabled) {
      if (hostConfirmEnabled) {
        setAwaitingHostConfirm(true);
      } else {
        handleJoin(config.displayName);
      }
      return;
    }

    (async () => {
      // Otimizacao "skip deviceCheck quando permissoes ja granted" SO se aplica
      // pro medico. Motivo: paciente entra via link com displayName na URL,
      // useEffect dispara handleJoin sem user gesture explicito do paciente
      // (so abrir o link nao conta como gesto dentro do iframe). Mesmo com
      // Permissions API reportando 'granted', getUserMedia em iframe SEM
      // user gesture pode ser negado com NotAllowedError - confirmado em
      // prod (Robert Pereira da Silva 08:38). Forcando deviceCheck pro
      // paciente, ele clica no botao "Ativar camera/microfone" que serve
      // como user gesture e o getUserMedia interno passa. Pro medico, que
      // tem permissoes ja granted no PC dele de consultas anteriores e
      // rotina de uso frequente, mantemos a otimizacao.
      const cached = isHost && await checkCachedPermissions();
      if (cached) {
        postEmbedEvent('permissions_changed', { microphone: true, camera: true, source: 'cached' });
        if (hostConfirmEnabled) {
          setAwaitingHostConfirm(true);
        } else {
          await handleJoin(config.displayName);
        }
      } else {
        setAwaitingDeviceCheck(true);
      }
    })();
  }, [loading, roomInfo, config.displayName, joined, error]); // eslint-disable-line

  // Tracker do tempo decorrido enquanto loading=true. Tick de 500ms basta
  // pros 3 thresholds (3s/7s) sem custo. Reset pra 0 quando loading vai
  // pra false - cobre tanto o caso de sucesso (entra na sala) quanto o
  // de erro (cai pra tela "Tentar novamente"); nos dois, a mensagem
  // proxima vez recomeca do zero.
  useEffect(() => {
    if (!loading) {
      setLoadingElapsedMs(0);
      return undefined;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      setLoadingElapsedMs(Date.now() - start);
    }, 500);
    return () => clearInterval(interval);
  }, [loading]);

  const runDeviceCheck = async () => {
    setDeviceCheck(s => ({ ...s, checking: true, errorMessage: '' }));
    let gotMic = false, gotCam = false;
    // getUserMedia as vezes pendura indefinidamente em Chrome Android quando
    // a permissao ficou num estado intermediario (negada temporariamente por
    // politica de site, driver travado, camera em uso por outro app sem erro
    // ainda ter propagado). Sem timeout, a tela "Testando..." fica pra
    // sempre. 10s e longo o bastante pra aparelho lento conceder e curto o
    // bastante pra o usuario nao achar que travou.
    const DEVICE_CHECK_TIMEOUT_MS = 10_000;
    try {
      // Se ja temos um stream do check anterior (usuario clicou novamente),
      // libera antes de abrir novo - getUserMedia em alguns drivers (Realtek,
      // mics USB baratos) recusa abrir um segundo handle do mesmo device.
      if (deviceStreamRef.current) {
        deviceStreamRef.current.getTracks().forEach(t => t.stop());
        deviceStreamRef.current = null;
      }
      const stream = await Promise.race([
        // IMPORTANTE: aplicar o MESMO frameRate cap aqui que useLiveKit usa em
        // videoCaptureDefaults. Sem isso, esse getUserMedia abre a camera no
        // modo nativo do device (60fps em iPhone/Galaxy/Mac modernos), o
        // browser/driver "lembra" desse modo, e quando o LiveKit chama
        // enableCameraAndMicrophone DEPOIS o stream volta a 60fps mesmo com o
        // constraint de 30 - o driver nao re-aplica constraints na mesma
        // sessao de processo. Confirmado em prod (sessao 01/05 10:07 -
        // doutor 23fps mas paciente que passou pelo deviceCheck saiu em
        // 70fps). Capar aqui garante coerencia em todos os caminhos.
        navigator.mediaDevices.getUserMedia({
          audio: true,
          // facingMode: 'user' (frontal) e o padrao em videochamada -
          // sem essa flag, Android escolhe camera traseira que pode estar
          // coberta pela capa, suja, ou em uso por outro app -> erro.
          // useLiveKit usa o mesmo facingMode no publish.
          video: { frameRate: { ideal: 30, max: 30 }, facingMode: 'user' },
        }),
        new Promise((_, reject) => setTimeout(
          () => reject(Object.assign(new Error('getUserMedia timeout'), { name: 'TimeoutError' })),
          DEVICE_CHECK_TIMEOUT_MS,
        )),
      ]);
      gotMic = stream.getAudioTracks().length > 0;
      gotCam = stream.getVideoTracks().length > 0;
      // NAO chamamos stream.getTracks().forEach(t => t.stop()) aqui de
      // proposito. Liberar o hardware do mic/camera entre o check e o
      // momento em que o LiveKit chama enableCameraAndMicrophone() criava
      // uma janela onde o browser fecha o device e reabre em outro
      // contexto - o primeiro pacote RTP publicado pro SFU saia mudo
      // (issue livekit/livekit#4379, suitenumerique/meet#1152, sintoma
      // confirmado em ~5-10% das nossas chamadas). Telemedicinanew
      // mantem o stream ativo entre permission check e publish - mesmo
      // padrao aqui. O ref e limpo no cleanup useEffect quando o
      // componente desmonta (paciente saiu da sala).
      deviceStreamRef.current = stream;
      setDeviceCheck({ micGranted: gotMic, camGranted: gotCam, checking: false, errorMessage: '' });
      postEmbedEvent('permissions_changed', { microphone: gotMic, camera: gotCam });
      logDiagnostic({
        severity: gotMic && gotCam ? 'info' : 'warn',
        type: 'device_check',
        message: `mic=${gotMic} cam=${gotCam}`,
        metadata: { microphone: gotMic, camera: gotCam },
      });
      return gotMic && gotCam;
    } catch (err) {
      const name = err?.name || '';
      let msg = 'Nao foi possivel acessar a camera ou o microfone.';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        msg = 'Voce bloqueou o acesso a camera ou ao microfone. Permita o acesso nas configuracoes do navegador e tente novamente.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        msg = 'Nao encontramos nenhuma camera ou microfone no seu dispositivo. Conecte os equipamentos e tente novamente.';
      } else if (name === 'NotReadableError') {
        msg = 'A camera ou o microfone estao sendo usados por outro programa. Feche-o e tente novamente.';
      } else if (name === 'TimeoutError') {
        msg = 'O navegador nao respondeu ao pedir acesso a camera/microfone. Recarregue a pagina e tente novamente.';
      }
      setDeviceCheck({ micGranted: false, camGranted: false, checking: false, errorMessage: msg });
      postEmbedEvent('permissions_changed', { microphone: false, camera: false, error: name || 'unknown' });
      logDiagnostic({
        severity: 'error',
        type: 'device_check_failed',
        message: msg,
        metadata: { errorName: name, errorMessage: err?.message },
      });
      return false;
    }
  };

  const handleContinueAfterCheck = async () => {
    setAwaitingDeviceCheck(false);
    await handleJoin(config.displayName);
  };

  // Cleanup do stream do deviceCheck quando o componente desmonta. Sem isso,
  // o mic/camera ficariam segurados mesmo depois do paciente sair da sala
  // (ate o garbage collector rodar) - icone de "gravando" continuaria
  // aceso na aba ate o usuario fechar.
  useEffect(() => {
    return () => {
      if (deviceStreamRef.current) {
        deviceStreamRef.current.getTracks().forEach(t => t.stop());
        deviceStreamRef.current = null;
      }
    };
  }, []);

  const handleJoin = async (name) => {
    const n = (name || userName || '').trim();
    if (!n) return;
    setLoading(true);
    try {
      const body = { displayName: n, role: config.role };
      if (config.roomKey) body.roomKey = config.roomKey;
      if (config.externalId)  body.externalId  = config.externalId;
      if (config.crmConselho) body.crmConselho = config.crmConselho;
      if (config.crmNumero)   body.crmNumero   = config.crmNumero;
      if (config.crmEstado)   body.crmEstado   = config.crmEstado;

      // /join com retry expo. 4G instavel costuma falhar a primeira ou as
      // duas primeiras e fecha; pra o paciente vale insistir. Erros 4xx do
      // backend (sala cheia, expirada) nao sao retriados - aborta na hora
      // com a mensagem especifica pra aparecer pro usuario.
      const joinOnce = () => fetch(`${window.location.origin}/api/public/join/${roomId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const maxAttempts = 3;
      const backoffMs = [0, 800, 2_000];
      let r = null;
      let lastErr = null;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (backoffMs[attempt] > 0) await new Promise(res => setTimeout(res, backoffMs[attempt]));
        try {
          r = await joinOnce();
          // Sucessos e erros de contrato (4xx) nao retriam - so 5xx/network.
          if (r.ok || (r.status >= 400 && r.status < 500)) break;
          lastErr = new Error(`HTTP ${r.status}`);
        } catch (netErr) {
          lastErr = netErr;
        }
      }
      if (!r) {
        setError('Nao foi possivel conectar ao servidor.');
        logDiagnostic({
          severity: 'error',
          type: 'join_network_failure',
          message: lastErr?.message || 'Falha de rede apos retry',
          metadata: { attempts: maxAttempts },
        });
        return;
      }
      const data = await r.json();
      if (data.error) {
        setError(data.error);
        logDiagnostic({
          severity: 'error',
          type: 'join_rejected',
          message: data.error,
          metadata: { httpStatus: r.status },
        });
        return;
      }

      if (data.chatToken) setChatToken(data.chatToken);
      if (data.participantIdentity) setMyIdentity(data.participantIdentity);
      if (data.role) setEffectiveRole(data.role);

      // Host tem preferencia persistida no DoctorProfile. Paciente segue
      // com o que ja veio do localStorage (inicializacao). null do backend
      // = medico nunca decidiu, mantem o default (false).
      if (data.role === 'host' && typeof data.pipAutoEnabled === 'boolean') {
        setPipAutoEnabled(data.pipAutoEnabled);
      }

      // Completa o contexto de diagnostico com identity/role reais do backend -
      // dai toda log subsequente (do useLiveKit etc) ja vai carregando isso.
      setDiagnosticsContext({
        identity: data.participantIdentity || null,
        role: data.role || config.role,
      });
      logDiagnostic({
        severity: 'info',
        type: 'join_ok',
        message: `/join 200 role=${data.role}`,
        metadata: { hasBackground: !!data.backgroundUrl },
      });

      // connect() agora propaga falhas (auth, ws, sala inexistente no SFU).
      // Antes a excecao era engolida em useLiveKit e o paciente caia em
      // setJoined(true) numa UI funcional sobre um engine morto - cliques
      // em camera/mic falhavam com "engine not connected within timeout"
      // e a unica saida era fechar a aba e reabrir. Agora a falha vira
      // tela de erro com botao "Tentar novamente" (que recarrega).
      //
      // Auto-retry silencioso: a 1a tentativa de connect falha
      // frequentemente em dois cenarios distintos mas com mesma cura:
      //   (a) Cold cache - 1a conexao do dispositivo no dia/sessao. DNS,
      //       TLS handshake, credenciais TURN, conexao HTTP/2 com backend
      //       e stack WebRTC do browser estao todos "frios" e podem
      //       estourar timeout. Pacientes (que sao sempre 1a sessao)
      //       sentem isso 100% das vezes; medicas so na 1a do dia.
      //   (b) iOS Safari + UDP bloqueado - operadora/firewall corta UDP,
      //       iOS Safari demora 10s pra desistir e cair pra TCP. 2a
      //       tentativa ja vai direto pra TCP cacheado.
      // Em ambos, a 2a/3a tentativa quase sempre conecta. Sem retry, o
      // usuario ve "Nao conseguimos conectar" e tem que clicar manual.
      // Com retry silencioso, a UI fica em loading uns segundos a mais
      // e a falha transitoria some. Erros nao-retriavel (permissao
      // negada, token invalido) abortam cedo pra nao prender o usuario.
      // Pre-construir o VirtualBackground processor ANTES do connect pra
      // passar pro useLiveKit aplicar na track de camera ANTES do publish.
      // Antes a gente publicava sem processor e chamava setProcessor()
      // depois - isso disparava renegociacao SDP que com singlePeerConnection
      // (default) baguncava transceivers e fazia subscribers (peer que entra
      // depois) chegarem com MST de audio stuck muted. Aplicando antes do
      // publish, sem renegociacao, sem bug.
      let backgroundProcessor = null;
      const bgFullUrl = data.backgroundUrl
        ? (data.backgroundUrl.startsWith('http') ? data.backgroundUrl : `${window.location.origin}${data.backgroundUrl}`)
        : null;
      const shouldAutoApplyBg = bgFullUrl && data.backgroundEnabled !== false;
      if (shouldAutoApplyBg) {
        try {
          // Pre-flip pra compensar o scaleX(-1) CSS no tile remoto/PiP
          // (texto/logo no fundo n sai invertido pro outro lado).
          const flipped = await flipImageHorizontally(bgFullUrl);
          backgroundProcessor = VirtualBackground(flipped);
        } catch (e) {
          // Falha ao gerar o processor (image fetch, decode, etc) - segue
          // sem background. O publish vai sair sem ele - melhor isso do
          // que abortar a chamada toda.
          logDiagnostic({
            severity: 'error',
            type: 'background_prepare_failed',
            message: e?.message || 'Falha ao preparar VirtualBackground pre-connect',
            metadata: { url: bgFullUrl, name: e?.name },
          });
        }
      }

      const connectMaxAttempts = 3;
      // Backoff curto entre tentativas. Quando o ICE timeout do LiveKit
      // (peerConnectionTimeout=7000ms em useLiveKit.connect) decide que
      // UDP nao vai resolver, queremos tentar de novo o quanto antes -
      // a 2a tentativa quase sempre pega TCP de cara. Backoff existe so
      // pra dar tempo do SDK limpar o Room antigo (~150ms) e nao
      // empilhar handshakes em paralelo.
      const connectBackoffMs = [0, 500, 1500];
      let connectErr = null;
      for (let attempt = 0; attempt < connectMaxAttempts; attempt++) {
        if (connectBackoffMs[attempt] > 0) {
          await new Promise(res => setTimeout(res, connectBackoffMs[attempt]));
          logDiagnostic({
            severity: 'warn',
            type: 'connect_silent_retry',
            message: `Retry silencioso ${attempt + 1}/${connectMaxAttempts} apos falha`,
            metadata: {
              attempt: attempt + 1,
              previousError: connectErr?.message || null,
              previousErrorName: connectErr?.name || null,
            },
          });
        }
        try {
          // Filtra iceServers baseado em ?testIce= (sandbox/debug). Em
          // producao normal (testIce ausente), passa todos sem mudar.
          // Modos:
          //   cloudflare - so URLs do *.cloudflare.com + forca relay
          //   coturn     - so URLs do *.franciscooliveira.online + forca relay
          //   no-turn    - zera iceServers (forca conexao direta)
          // forceRelay: passa iceTransportPolicy=relay pro RTCPeerConnection,
          // pra browser ser obrigado a passar pelo TURN escolhido em vez de
          // fazer direct quando UDP esta livre.
          let testedIce = data.iceServers;
          let forceRelay = false;
          if (config.testIce && Array.isArray(testedIce)) {
            const before = testedIce.length;
            if (config.testIce === 'cloudflare') {
              testedIce = testedIce.filter(s => (s.urls || []).some(u => u.includes('cloudflare.com')));
              forceRelay = true;
            } else if (config.testIce === 'coturn') {
              testedIce = testedIce.filter(s => (s.urls || []).some(u => u.includes('franciscooliveira.online')));
              forceRelay = true;
            } else if (config.testIce === 'no-turn') {
              testedIce = [];
              // Sem TURN nao faz sentido forcar relay - sem servidor pra relayar.
            }
            logDiagnostic({
              severity: 'info',
              type: 'test_ice_filter',
              message: `testIce=${config.testIce} reduziu iceServers de ${before} pra ${testedIce.length}${forceRelay ? ' + forceRelay' : ''}`,
              metadata: {
                mode: config.testIce,
                before,
                after: testedIce.length,
                forceRelay,
                urls: (testedIce || []).flatMap(s => s.urls || []),
              },
            });
          }
          // Reusa o stream do deviceCheck quando existir (paciente). Sem isso,
          // o LiveKit chamaria getUserMedia DE NOVO ~30s depois do deviceCheck,
          // e o driver Android tipicamente devolve NotReadableError nesse cenario
          // (handle da camera caiu em estado idle apos o stop, e ap. um
          // room.connect que falhou no meio nao consegue reabrir cleanly).
          // Passando o stream existente, LiveKit publica direto - 1 unica
          // aquisicao na sessao, sem janela pra driver bagunçar. Medico (sem
          // deviceCheck) passa undefined - useLiveKit cai no createLocalTracks.
          //
          // CRITICO: NAO limpar deviceStreamRef.current aqui dentro do loop
          // de retry. Se a 1a tentativa falhou em room.connect (ICE timeout),
          // o stream nem chegou a ser usado pela LocalVideoTrack/AudioTrack -
          // continua vivo na memoria. Limpando o ref aqui, a 2a tentativa
          // (retry) recebe preAcquired=null e cai no createLocalTracks que
          // faz getUserMedia novo - NotReadableError no Android (caso Helen
          // 09:18 confirmado em prod). Limpamos so depois do break (sucesso).
          await connect(data.token, data.wsUrl, testedIce, forceRelay ? { forceRelay: true } : undefined, backgroundProcessor, deviceStreamRef.current);
          // Sucesso: stream foi efetivamente transferido pro LiveKit, cleanup
          // useEffect nao deve mais stoppar.
          deviceStreamRef.current = null;
          connectErr = null;
          break;
        } catch (e) {
          connectErr = e;
          // Erros nao-retriavel: permissao negada (browser bloqueou
          // camera/mic) ou token invalido (sessao expirou, sala fechou).
          // Repetir nao adianta - aborta agora pra usuario ver mensagem
          // clara em vez de esperar 4-5s extra de retries inuteis.
          const msg = e?.message || '';
          if (e?.name === 'NotAllowedError' || /token/i.test(msg)) break;
        }
      }
      if (connectErr) {
        setError('Nao conseguimos conectar a sala. Tente novamente.');
        logDiagnostic({
          severity: 'error',
          type: 'join_connect_failed',
          message: connectErr?.message || 'Falha ao conectar ao LiveKit apos /join',
          metadata: { name: connectErr?.name, attemptsMade: connectMaxAttempts },
        });
        return;
      }
      if (config.startAudioOff) await toggleMic();
      if (config.startVideoOff) await toggleCamera();

      // Auto-aplica virtual background se o backend retornou um (medico com template).
      // Guarda a URL em state pra o botao Fundo poder reaplicar depois de toggle off.
      // Respeita backgroundEnabled do medico: se ele desligou em outra consulta
      // (=== false), guarda a URL mas NAO aplica. null = nunca decidiu, aplica.
      //
      // Loga o desfecho em todas as pontas pra facilitar diagnostico no admin
      // quando medico reclama de "nao apareceu o fundo". O backend classifica em
      // applied / disabled_by_pref / no_template / generation_failed / no_external_id.
      // "Medico nao recebeu o fundo" costuma bater em no_template (template do
      // cliente nao foi cadastrado) ou generation_failed (erro de geracao da
      // imagem - fonte, unicode, composicao) - os dois aparecem como background_missing
      // no painel com o status no metadata.
      // Estado do template/fundo pos-connect. Aplicacao do processor ja
      // aconteceu PRE-publish dentro de connect() (pra evitar renegociacao
      // SDP que disparava o bug do audio mute). Aqui so atualizamos o
      // state local pra o botao "Fundo" funcionar e o admin saber o que
      // aconteceu.
      const bgStatus = data.backgroundStatus;
      if (bgFullUrl) {
        setTemplateUrl(bgFullUrl);
        if (shouldAutoApplyBg) {
          if (backgroundProcessor) {
            setTemplateActive(true);
            logDiagnostic({
              severity: 'info',
              type: 'background_applied',
              message: 'Fundo aplicado pre-publish',
              metadata: { url: bgFullUrl },
            });
          }
          // Se backgroundProcessor for null aqui, e porque o
          // background_prepare_failed ja foi logado antes do connect.
        } else {
          logDiagnostic({
            severity: 'info',
            type: 'background_skipped',
            message: 'Fundo nao aplicado por preferencia do medico',
            metadata: { backgroundEnabled: data.backgroundEnabled },
          });
        }
      } else if (effectiveRole === 'host' || data.role === 'host') {
        logDiagnostic({
          severity: 'warn',
          type: 'background_missing',
          message: `Fundo nao gerado (status=${bgStatus || 'desconhecido'})`,
          metadata: {
            status: bgStatus,
            serverError: data.backgroundError || null,
            externalId: config.externalId || null,
            hasCrm: !!(config.crmNumero && config.crmEstado),
          },
        });
      }

      setJoined(true);
      endEmittedRef.current = false;
      // Marca o inicio da consulta agora. Primeiro join ganha; se o mesmo
      // componente rejoin'ar (improvavel mas possivel), mantem o inicio
      // original pra duracao refletir a consulta real.
      if (!sessionStartedAtRef.current) {
        const now = Date.now();
        sessionStartedAtRef.current = now;
        setSessionStartedAt(now);
      }
      postEmbedEvent('ready', { roomId, role: data.role, identity: data.participantIdentity });
    } catch (err) {
      console.error(err);
      setError('Erro ao entrar na sala.');
      logDiagnostic({
        severity: 'error',
        type: 'join_error',
        message: err?.message || 'Erro ao entrar na sala',
        metadata: { stack: err?.stack },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLeave = async () => {
    postEmbedEvent('leave', { roomId });
    const isHost = effectiveRole === 'host';

    if (isHost) {
      // Host encerrando a sala: pede pro backend fechar pra TODOS. O LiveKit
      // dispara Disconnected/ROOM_DELETED em cada cliente, e o EmbedPage de
      // cada um renderiza a tela de "Atendimento encerrado" via emitEndOnce.
      try { await publicApi.endRoom(roomId, chatToken); }
      catch (err) { console.warn('Falha ao encerrar sala:', err); }
      emitEndOnce('host_ended');
      disconnect();
    } else {
      // Paciente so sai localmente, a sala continua ativa.
      disconnect();
      setJoined(false);
    }
  };

  // bfcache (back-forward cache): browsers modernos "congelam" a aba quando
  // o usuario navega away, e a "descongelam" ao voltar (history.back, restaurar
  // aba, etc). React state fica intacto, MAS a WebSocket do LiveKit e os
  // MediaStreamTracks costumam morrer enquanto congelados. Ao descongelar o
  // <video> fica preto, audio parado e nada recupera sozinho.
  //
  // Estrategia:
  //   - pagehide com persisted=true: indo pra bfcache. Desconecta do LiveKit
  //     pra o servidor nao ficar esperando um participante que nao volta no
  //     mesmo socket. Custo baixo: se o usuario voltar, o reload abaixo
  //     recria tudo do zero.
  //   - pageshow com persisted=true: voltando do bfcache. Como desconectamos
  //     no pagehide, a rota mais confiavel e recarregar a pagina. O React
  //     monta limpo, useLiveKit chama connect() de novo, e o backend emite
  //     os tokens novos via /join. Bem mais estavel do que tentar remendar
  //     tracks mortos.
  useEffect(() => {
    if (!joined) return undefined;
    const handlePageHide = (e) => {
      if (e.persisted && typeof disconnect === 'function') {
        try { disconnect(); } catch { /* room pode ja ter caido */ }
      }
    };
    const handlePageShow = (e) => {
      if (e.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [joined, disconnect]);

  // Refresh silencioso do chatToken. O token emitido no /join vive 1h - o
  // cliente bate em /refresh-token ~5min antes de vencer pra nao deixar o
  // REST de chat e o SignalR hub caindo em 401 mid-consulta. A sessao de
  // media do LiveKit se mantem ativa independente (o SFU nao re-valida o
  // token em conexoes ja estabelecidas), entao so precisamos garantir que
  // os endpoints HTTP tenham credencial fresca.
  //
  // Agenda o primeiro refresh com 55min depois do join e depois a cada 55min.
  // Em caso de falha (5xx, rede), tenta de novo em 1min - ate o token atual
  // vencer, depois para.
  useEffect(() => {
    if (!joined || !chatToken) return undefined;
    let timerId = null;
    let cancelled = false;

    const scheduleRefresh = (delayMs) => {
      timerId = setTimeout(async () => {
        if (cancelled) return;
        try {
          const res = await publicApi.refreshToken(roomId, chatToken);
          if (cancelled) return;
          if (res.ok && res.chatToken) {
            setChatToken(res.chatToken);
            logDiagnostic({
              severity: 'info',
              type: 'token_refreshed',
              message: 'chatToken renovado com sucesso',
            });
            scheduleRefresh(55 * 60 * 1000);
          } else {
            logDiagnostic({
              severity: 'warn',
              type: 'token_refresh_failed',
              message: `refresh retornou status=${res.status || 'n/a'}`,
              metadata: { status: res.status },
            });
            // 1min de backoff pra tentar de novo - se o token atual vencer
            // antes, o 401 no proximo request vai forcar relogin.
            scheduleRefresh(60 * 1000);
          }
        } catch (err) {
          logDiagnostic({
            severity: 'warn',
            type: 'token_refresh_error',
            message: err?.message || 'erro no refresh-token',
          });
          scheduleRefresh(60 * 1000);
        }
      }, delayMs);
    };

    scheduleRefresh(55 * 60 * 1000);
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [joined, chatToken, roomId]);

  // Comportamento do botao Fundo varia por role:
  //   host com templateUrl  -> toggle do fundo personalizado da marca
  //   participant ou sem template -> toggle do BackgroundBlur
  // O processor ativo e trocado pelo useLiveKit.setProcessor, que para o antigo
  // antes de aplicar o novo (evita Stream closed / piscar de imagem).
  // Toggle do fundo personalizado do medico. Clica 1x: desativa (camera
  // natural). Clica de novo: reativa o template. Paciente nao tem essa funcao
  // - o botao Fundo so e visivel pro host com template carregado.
  const handleToggleBackground = async () => {
    if (!templateUrl) return;
    const next = !templateActive;
    if (next) {
      const flipped = await flipImageHorizontally(templateUrl);
      await setProcessor(VirtualBackground(flipped));
      setTemplateActive(true);
    } else {
      await setProcessor(null);
      setTemplateActive(false);
    }
    // Persiste pra que a proxima consulta respeite a escolha. Fire-and-forget:
    // se o POST cair, a UI ja refletiu o estado novo e a preferencia sera
    // lembrada so da proxima vez que o medico tocar no botao.
    if (chatToken) {
      publicApi.setBackgroundPreference(roomId, chatToken, next).catch(() => {});
    }
  };

  // Minimizar (PiP) continua sendo botao manual - medico clica na hora que
  // quer abrir/fechar. Auto-PiP (atributo autoPictureInPicture no <video>) e
  // gerenciado pelo admin via DoctorsTab no painel; medico nao tem toggle
  // proprio - evita o conflito de "ativei sem querer e agora tudo abre em
  // PiP" e padroniza comportamento pra toda a clinica.
  const handleTogglePip = async () => {
    try { await remoteVideoRef.current?.togglePip(); } catch { /* browser recusou */ }
  };


  const backgroundActive = templateActive;

  const bgClass = config.transparentBg ? 'bg-transparent' : 'bg-gray-900';

  if (loading) {
    // Mensagem dinamica de feedback. Os 3 thresholds (3s, 7s) batem com
    // os pontos do retry silencioso: ate 3s e a 1a tentativa sendo
    // negociada; entre 3-7s ja entrou no peerConnectionTimeout do
    // LiveKit; depois de 7s ja estamos no auto-retry pegando rota
    // alternativa (TCP). Em conexao saudavel, a UI passa direto pelo
    // 1o estagio e nao chega aos outros.
    const loadingMessage = loadingElapsedMs < 3_000
      ? 'Conectando à sala...'
      : loadingElapsedMs < 7_000
        ? 'Verificando conexão...'
        : 'Quase lá, só mais um instante...';
    return (
      <div className={`min-h-dvh ${bgClass} flex items-center justify-center`}>
        <div className="flex flex-col items-center gap-4 px-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <p className="text-white text-sm">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  if (error) return (
    <div className={`min-h-dvh ${bgClass} flex items-center justify-center text-white p-4`}>
      <div className="text-center max-w-sm">
        <p className="text-xl mb-6">{error}</p>
        {/* Reload completo: o paciente ja fazia isso na mao (fechar aba e
            voltar pelo link) - botao automatiza. Cobre os casos em que
            o /join falhou por sala ainda nao pronta no SFU, WS killed por
            proxy idle, ou qualquer transiente de rede de celular. */}
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 rounded-xl font-semibold"
        >
          Tentar novamente
        </button>
      </div>
    </div>
  );

  // Tela final - exibida quando o medico encerrou a chamada server-side.
  // Parent page (HelpGlobal) deveria ja ter redirecionado o paciente via
  // postMessage end_meeting; isso e fallback pra quando ninguem escuta.
  // Medico ve duracao da consulta; paciente so ve o aviso padrao.
  if (meetingEnded) {
    const isHost = effectiveRole === 'host';
    const durationMs = (sessionStartedAt && sessionEndedAt)
      ? sessionEndedAt - sessionStartedAt
      : null;
    return (
      <div className={`min-h-dvh ${bgClass} flex items-center justify-center p-4 text-white`}>
        <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl text-center">
          {roomInfo?.branding?.logo && (
            <img src={roomInfo.branding.logo} alt="" className="h-12 mx-auto mb-4" />
          )}
          <div className="w-14 h-14 rounded-full bg-emerald-600/20 flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                 strokeLinejoin="round" className="text-emerald-400">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2">Atendimento encerrado</h2>
          {isHost && durationMs != null ? (
            <>
              <p className="text-gray-400 text-sm mb-4">
                Consulta finalizada com sucesso.
              </p>
              <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4 mb-2">
                <div className="text-xs uppercase text-gray-500 tracking-wide mb-1">
                  Tempo de atendimento
                </div>
                <div className="text-3xl font-bold text-emerald-400 font-mono">
                  {formatDuration(durationMs)}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {formatClock(sessionStartedAt)} -- {formatClock(sessionEndedAt)}
                </div>
              </div>
            </>
          ) : (
            <p className="text-gray-400 text-sm">
              A consulta foi finalizada pelo medico. Voce ja pode fechar esta janela.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Tela de confirmacao "Entrar" pro medico (so quando o cliente habilitou
  // HostJoinConfirmEnabled e o medico ja tem permissoes de camera+mic).
  if (awaitingHostConfirm && !joined) {
    return (
      <div className={`min-h-dvh ${bgClass} flex items-center justify-center p-4`}>
        <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl text-white text-center">
          {roomInfo?.branding?.logo && (
            <img src={roomInfo.branding.logo} alt="" className="h-12 mx-auto mb-4" />
          )}
          <h2 className="text-xl font-bold mb-1">Ola, {config.displayName}</h2>
          <p className="text-gray-400 text-sm mb-4">
            Camera e microfone ja estao autorizados neste navegador.
            Clique em Entrar quando estiver pronto para iniciar a consulta.
          </p>
          <div className="mb-4">
            <PatientPresenceBadge present={patientPresent} />
          </div>
          <button
            onClick={() => {
              setAwaitingHostConfirm(false);
              handleJoin(config.displayName);
            }}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl font-semibold"
            style={{ backgroundColor: roomInfo?.branding?.primaryColor || undefined }}
          >
            Entrar
          </button>
        </div>
      </div>
    );
  }

  // Tela de pre-check de dispositivos (quando veio displayName na URL)
  if (awaitingDeviceCheck && !joined) {
    const support = roomInfo?.support;
    const allOk = deviceCheck.micGranted === true && deviceCheck.camGranted === true;
    return (
      <div className={`min-h-dvh ${bgClass} flex items-center justify-center p-4`}>
        <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl text-white">
          {roomInfo?.branding?.logo && (
            <img src={roomInfo.branding.logo} alt="" className="h-12 mx-auto mb-4" />
          )}
          <h2 className="text-center text-xl font-bold mb-1">Ola, {config.displayName}</h2>
          <p className="text-center text-gray-400 text-sm mb-6">
            Antes de entrar na consulta, vamos testar sua camera e microfone.
          </p>

          <div className="space-y-3 mb-6">
            <DeviceStatusRow
              label="Microfone"
              state={deviceCheck.micGranted}
            />
            <DeviceStatusRow
              label="Camera"
              state={deviceCheck.camGranted}
            />
          </div>

          {isHost && <div className="mb-4"><PatientPresenceBadge present={patientPresent} /></div>}

          {deviceCheck.errorMessage && (
            <div className="bg-red-900/40 border border-red-700 text-red-200 rounded-lg p-3 text-sm mb-4">
              {deviceCheck.errorMessage}
              <div className="text-xs mt-2 opacity-80">
                Dica: no Chrome, clique no cadeado ao lado da URL → Permissoes → permita camera e microfone.
              </div>
            </div>
          )}

          {deviceCheck.micGranted === null ? (
            <button
              onClick={runDeviceCheck}
              disabled={deviceCheck.checking}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl font-semibold disabled:opacity-50"
              style={{ backgroundColor: roomInfo?.branding?.primaryColor || undefined }}
            >
              {deviceCheck.checking ? 'Testando...' : 'Ativar camera e microfone'}
            </button>
          ) : allOk ? (
            <button
              onClick={handleContinueAfterCheck}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 rounded-xl font-semibold"
            >
              ✓ Tudo pronto - entrar na consulta
            </button>
          ) : (
            // Permissoes negadas: re-chamar getUserMedia nao adianta - browser
            // ja registrou o block e devolve a mesma rejeicao sem prompt.
            // Pra paciente que nao sabe ir nas Configuracoes do browser
            // desbloquear, isso virava beco sem saida. Em vez disso, manda
            // direto pra sala (handleContinueAfterCheck -> handleJoin) -
            // paciente entra sem video/audio publicado e o medico orienta
            // como liberar a permissao pelo chat ou audio.
            <button
              onClick={handleContinueAfterCheck}
              className="w-full py-3 bg-amber-600 hover:bg-amber-500 rounded-xl font-semibold"
            >
              Tentar novamente
            </button>
          )}

          {support?.enabled && support?.phone && deviceCheck.micGranted === false && (
            <div className="mt-5 bg-slate-700/50 border border-slate-600 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-300 mb-2">
                Ainda com problema? Entre em contato com o suporte:
              </p>
              <a
                href={`tel:${String(support.phone).replace(/\D/g, '')}`}
                className="inline-block bg-slate-900 hover:bg-slate-950 text-white px-5 py-2 rounded-lg font-mono text-lg"
              >
                📞 {support.phone}
              </a>
              <div className="mt-2">
                <a
                  href={`https://wa.me/${String(support.phone).replace(/\D/g, '')}`}
                  target="_blank" rel="noreferrer"
                  className="text-xs text-emerald-400 hover:underline"
                >
                  Falar no WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!joined) return (
    <div className={`min-h-dvh ${bgClass} flex items-center justify-center`}>
      <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
        {roomInfo?.branding?.logo && (
          <img src={roomInfo.branding.logo} alt="" className="h-12 mx-auto mb-4" />
        )}
        <h1 className="text-white text-2xl font-bold text-center mb-2">
          {roomInfo?.displayName || 'Sala de Video'}
        </h1>
        <p className="text-gray-400 text-center mb-6 text-sm">
          {roomInfo?.branding?.clientName || 'Video Call'}
        </p>

        <div className="space-y-4">
          <input
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
            placeholder="Seu nome"
            className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-500"
            autoFocus
          />
          {isHost && <div className="mb-1"><PatientPresenceBadge present={patientPresent} /></div>}
          <button
            onClick={() => handleJoin()}
            disabled={!userName.trim()}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50"
            style={{ backgroundColor: roomInfo?.branding?.primaryColor || undefined }}
          >
            Entrar na Sala
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`h-dvh ${bgClass} flex flex-col`}>
      <div className="flex-1 flex overflow-hidden">
        <div className={`flex-1 flex ${chatOpen ? 'mr-0' : ''}`}>
          <div className="flex-1 relative p-2">
            <VideoPlayer
              ref={remoteVideoRef}
              videoTrack={remoteVideoTrack}
              audioTrack={remoteAudioTrack}
              label={remoteName || 'Participante'}
              className="w-full h-full"
              micOn={remoteMic}
              camOn={remoteCam}
              mirror
              autoPip={pipAutoEnabled && effectiveRole === 'host'}
              reconnectEpoch={connectionEpoch}
              // Pro tile do paciente (visto pelo medico): usa patientPresent
              // do webhook em vez de isRemoteConnected do SDK. RoomEvent.
              // ParticipantConnected dispara cedo demais (durante handshake
              // do paciente) e fazia o tile aparecer com avatar "EP / Camera
              // desativada" antes dele realmente ter entrado. patientPresent
              // so vira true depois que o backend processou o webhook
              // participant_joined (LiveKitJoinedAt populado).
              // Pro tile do medico (visto pelo paciente): mantem
              // isRemoteConnected porque nao ha gate equivalente de webhook
              // pra presenca do medico, e o paciente nao reclamou desse caso.
              participantConnected={effectiveRole === 'host' ? patientPresent : isRemoteConnected}
              placeholderText={effectiveRole === 'host'
                ? 'Aguardando paciente...'
                : 'Aguarde enquanto medico entra na sala...'}
            />

            <div className="absolute bottom-4 right-4 w-40 md:w-52 aspect-video z-10">
              <VideoPlayer
                videoTrack={localVideoTrack}
                muted
                label={config.displayName || userName || 'Voce'}
                className="w-full h-full border-2 border-gray-600 shadow-lg"
                micOn={localMic}
                camOn={localCam}
                mirror
              />
            </div>
            {!config.minimal && (
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'
                }`}></span>
                <span className="text-white text-xs bg-black/50 px-2 py-1 rounded">
                  {connectionStatus === 'connected' ? 'Conectado' : 'Conectando...'}
                </span>
                {/* Cronometro discreto pro medico. Mesmo estilo do badge
                    Conectado pra nao competir visualmente com o video.
                    Paciente nao ve - se ele ficasse vendo o tempo correndo
                    poderia gerar ansiedade ("ja ta na hora?"). */}
                {effectiveRole === 'host' && sessionStartedAt && (
                  <span
                    className="text-white/80 text-xs bg-black/50 px-2 py-1 rounded font-mono tabular-nums"
                    title="Tempo de atendimento"
                  >
                    {formatDuration(elapsedMs)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Chat is kept mounted while joined so the SignalR subscription stays
            alive even with the panel collapsed - that is what lets the unread
            counter tick while the doctor has chat closed. The panel width
            collapses to 0 via CSS when !chatOpen. */}
        {chatAvailable && chatToken && (
          <div className={`${chatOpen ? 'w-80' : 'w-0'} flex-shrink-0 overflow-hidden transition-[width]`}>
            <Chat
              roomId={roomId}
              userName={config.displayName || userName}
              userRole={effectiveRole || config.role}
              isOpen={chatOpen}
              onToggle={() => setChatOpen(false)}
              accentColor={roomInfo?.branding?.primaryColor}
              chatToken={chatToken}
              myIdentity={myIdentity}
              onRemoteMessage={(msg) => {
                if (chatOpen) return;
                // Paciente recebendo mensagem do medico: abre o chat
                // automaticamente em vez de so contar unread. Em telemed
                // a mensagem do medico costuma ser instrucao critica
                // (link de receita, orientacao de exame) que o paciente
                // nao pode perder por nao perceber o badge. Pro lado do
                // medico a gente NAO faz isso - so badge - pra nao
                // interromper enquanto ele atende.
                const senderIsHost = msg?.senderRole === 'host';
                const iAmParticipant = effectiveRole !== 'host';
                if (senderIsHost && iAmParticipant) {
                  setChatOpen(true);
                  setChatUnread(0);
                } else {
                  setChatUnread(n => n + 1);
                }
              }}
            />
          </div>
        )}
      </div>

      <Controls
        onToggleMic={toggleMic}
        onToggleCamera={toggleCamera}
        // Passa o estado real do mic/cam pra o Controls saber se mostra
        // "Audio" (ativo) ou "Liberar microfone" (paciente bloqueou).
        // Sem isso ele assumia ativo por default e paciente que entrou
        // com permissao bloqueada via os botoes verdes mesmo sem mic.
        micOn={localMic}
        camOn={localCam}
        onLeave={handleLeave}
        onToggleChat={() => {
          setChatOpen(prev => {
            if (!prev) setChatUnread(0);
            return !prev;
          });
        }}
        chatOpen={chatOpen}
        chatUnread={chatUnread}
        // Botao Fundo so no medico, e so se existe template personalizado pra
        // alternar. Paciente nao ve esse botao.
        onToggleBackground={effectiveRole === 'host' && templateUrl ? handleToggleBackground : null}
        backgroundActive={backgroundActive}
        onSwitchCamera={effectiveRole !== 'host' ? switchCamera : null}
        cameraFacing={cameraFacing}
        // PiP no menu: por enquanto so o host (medico) tem. Paciente em
        // mobile usando PiP estava criando confusao - clicava sem querer e
        // achava que tinha saido da consulta. Quando voltar, considerar
        // adicionar tooltip explicativo antes de re-habilitar pra paciente.
        onTogglePip={effectiveRole === 'host'
          && typeof document !== 'undefined'
          && document.pictureInPictureEnabled
          ? handleTogglePip
          : null}
        pipActive={pipActive}
        // Botao Auto foi removido da UI do medico - controle total vai pro
        // painel admin (DoctorsTab -> coluna Auto PiP). Mantendo os callbacks
        // null pra Controls nao renderizar o botao.
        onToggleAutoPip={null}
        autoPipEnabled={pipAutoEnabled}
        showChat={chatAvailable}
        // Botao Sair/Encerrar:
        //   - host: label "Encerrar" - chama EndRoom no backend, fecha a
        //     sala pra todos os participantes (LiveKit dispara ROOM_DELETED)
        //   - paciente: label "Sair" - so chama disconnect() local + posta
        //     'leave' pro iframe pai. HelpGlobal escuta esse evento e
        //     redireciona o paciente pra home (/minha-conta), entao ele nao
        //     "fica" numa tela intermediaria - sai direto da consulta.
        //     handleLeave ja distingue os dois casos pelo effectiveRole.
        showLeave={config.leaveButtonOn}
        leaveLabel={effectiveRole === 'host' ? 'Encerrar' : 'Sair'}
        showScreenshare={config.screenshareOn}
        minimal={config.minimal}
      />
    </div>
  );
}
